import { AxiosTransport } from '@ingenico/api-sdk-core/axios';
import { CoreClient } from '@ingenico/api-sdk-core/common';
import { mfeShellContainerInit, PreferenceSessionStorage } from '@ingenico/util-mfe-shell';
import type { CommonAndProjectEnvDataAndSchemas, EnvironmentData } from '@ingenico/util-mfe-shell/env';
import { CommonEnvKey, validateEnvSchema } from '@ingenico/util-mfe-shell/env';
import type { LazyContainerApi, LazyContainerConfig } from '@ingenico/util-mfe';
import { createLazyContainer } from '@ingenico/util-mfe';
import axios from 'axios';
import { logError } from '@ingenico/util-common';
import { genericWrapper } from '../../mfe/entrypoint';
import type { DeviceManagementEnvDataAndSchemas } from '../config.const';
import { createEnvironmentVariablesSchema } from '../config.const';
import { appMocksInit } from '../mocks';
import { UserTypeSwitchService } from '../services/userTypeSwitch.service';
import { StandaloneEnvFlagsService } from '../services/standaloneEnvFlags.service';
import { DeviceManagementSpiAdapter } from './adapters/deviceManagementSpi.adapter';
import { appContainer } from './appContainer.const';
import type { MfeConfiguration } from './hexa.model';

export const lazyContainerApiIt = 'LazyContainerApi';
export const envValuesIt = 'EnvironmentValues';

let isStandaloneMswFallbackActive = false;

export const getIsStandaloneMswFallbackActive = () => isStandaloneMswFallbackActive;

export async function appContainerInit(): Promise<void> {
  isStandaloneMswFallbackActive = false;
  const standaloneUserType = UserTypeSwitchService.getCurrentUserType();
  const resolvedEnv = StandaloneEnvFlagsService.buildEnvForUserType(standaloneUserType, process.env);
  const environmentData = validateEnvSchema(createEnvironmentVariablesSchema(resolvedEnv));
  const axiosInstance = axios.create({
    baseURL: environmentData[CommonEnvKey.ApiOrigin],
  });
  const authAxiosInstance = axios.create({
    baseURL: environmentData[CommonEnvKey.ApiOrigin],
  });
  const apiSdkClient = new CoreClient(
    new AxiosTransport(axiosInstance),
    !process.env.NODE_ENV || process.env.NODE_ENV === 'development',
  );

  // Allow standalone boot to continue with real APIs when MSW cannot register (e.g. local SSL trust issues).
  const apiSdkMockControl = await appMocksInit(environmentData[CommonEnvKey.ApiOrigin]).catch((error) => {
    isStandaloneMswFallbackActive = true;
    logError(error);
    return undefined;
  });
  const userPreferenceStorage = new PreferenceSessionStorage();

  mfeShellContainerInit(appContainer, {
    axiosInstance,
    authAxiosInstance,
    apiSdkClient,
    apiSdkMockControl,
    userPreferenceStorage,
  });
  appContainer.bind(DeviceManagementSpiAdapter).toSelf();

  const config: LazyContainerConfig<MfeConfiguration> = {
    DeviceManagement: {
      lazyLoadFunction: async () => ({ genericWrapper }),
      spi: () => appContainer.get(DeviceManagementSpiAdapter),
    },
  };

  const lazyContainerApi = createLazyContainer<MfeConfiguration>(config);

  appContainer.bind<LazyContainerApi<MfeConfiguration>>(lazyContainerApiIt).toConstantValue(lazyContainerApi);
  appContainer
    .bind<EnvironmentData<CommonAndProjectEnvDataAndSchemas<DeviceManagementEnvDataAndSchemas>>>(envValuesIt)
    .toConstantValue(environmentData);
}
